import React, { useState, useMemo, useEffect } from 'react';
import { Search, Filter, Download, ChevronLeft, ChevronRight, MessageSquare, Edit, Trash2, Eye, EyeOff, Heart } from 'lucide-react';
import { User } from '../../models/User';
import { PostActivity as PostActivityType, AdminActivityFilter, AdminActivityResponse } from '../../models/AdminActivity';
import { getPostActivities } from '../../data/adminActivityDatabase.ts';
import { 
  formatTimestamp, 
  formatActivityAction, 
  generatePageNumbers, 
  exportToCSV, 
  searchActivities,
  getActivityDetails,
  formatDateForFilename,
  getActionIcon as getSharedActionIcon,
  getActionColor as getSharedActionColor
} from '../../utils/adminUtils';
import { getPaginationButtonClasses, getContainerClasses } from '../../utils/styleUtils';
import { PAGINATION } from '../../config/constants';
import { MetricCard } from './MetricCard';

interface PostsActivityProps {
  user: User;
}

function PostsActivity({ user }: PostsActivityProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<25 | 50 | 100 | 250>(PAGINATION.DEFAULT_PAGE_SIZE);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [actionFilter, setActionFilter] = useState<string>('all');
  const [postTypeFilter, setPostTypeFilter] = useState<string>('all');
  const [activitiesData, setActivitiesData] = useState<AdminActivityResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load data with pagination
  useEffect(() => {
    const loadActivities = async () => {
      setIsLoading(true);
      try {
        const data = await getPostActivities(currentPage, pageSize);
        setActivitiesData(data);
      } catch (error) {
        console.error('Failed to load post activities:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadActivities();
  }, [currentPage, pageSize]);

  const postActivities = activitiesData?.activities as PostActivityType[] || [];
  const pagination = activitiesData?.pagination || { totalItems: 0, totalPages: 0, currentPage: 1, pageSize: 25 };

  // Apply filters
  const filteredActivities = useMemo(() => {
    let filtered = postActivities;
    
    if (actionFilter !== 'all') {
      filtered = filtered.filter(activity => activity.action === actionFilter);
    }
    
    if (postTypeFilter !== 'all') {
      filtered = filtered.filter(activity => activity.postType === postTypeFilter);
    }
    
    return searchActivities(filtered, searchTerm) as PostActivityType[];
  }, [postActivities, searchTerm, actionFilter, postTypeFilter]);

  const handleExport = () => {
    exportToCSV(filteredActivities, `posts-activities-${formatDateForFilename(new Date())}.csv`);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: 25 | 50 | 100 | 250) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const getActionIcon = (action: string) => getSharedActionIcon(action, 'post');
  const getActionColor = (action: string) => getSharedActionColor(action, 'post');

  // Calculate statistics
  const createActions = postActivities.filter(a => a.action === 'create').length;
  const editActions = postActivities.filter(a => a.action === 'edit').length;
  const deleteActions = postActivities.filter(a => a.action === 'delete').length;
  const reactionActions = postActivities.filter(a => a.action === 'react').length;
  const timelinePosts = postActivities.filter(a => a.postType === 'timeline').length;
  const meetingPosts = postActivities.filter(a => a.postType === 'meeting').length;

  // Memoize styling classes
  const containerClasses = useMemo(() => getContainerClasses('lg'), []);

  // Memoized pagination button classes
  const getPaginationButtonClass = useMemo(() => {
    return (isDisabled: boolean = false) => getPaginationButtonClasses(isDisabled);
  }, []);

  return (
    <div className={containerClasses}>
      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <MetricCard
          title="Created Posts"
          value={createActions}
          icon={MessageSquare}
          color="green"
        />
        <MetricCard
          title="Edited Posts"
          value={editActions}
          icon={Edit}
          color="blue"
        />
        <MetricCard
          title="Reactions"
          value={reactionActions}
          icon={Heart}
          color="purple"
        />
        <MetricCard
          title="Total Activities"
          value={pagination.totalItems}
          icon={MessageSquare}
          color="indigo"
        />
      </div>

      {/* Post Type Statistics */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <MetricCard
          title="Timeline Posts"
          value={timelinePosts}
          icon={MessageSquare}
          color="blue"
        />
        <MetricCard
          title="Meeting Posts"
          value={meetingPosts}
          icon={MessageSquare}
          color="green"
        />
      </div>

      {/* Controls */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Search and Filters */}
            <div className="flex-1 flex flex-col sm:flex-row gap-4">
              <div className="flex-1 max-w-lg">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Search by user, post title, or post ID..."
                  />
                </div>
              </div>
              
              <div className="flex gap-2">
                <select
                  value={actionFilter}
                  onChange={(e) => setActionFilter(e.target.value)}
                  className="block w-auto pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                >
                  <option value="all">All Actions</option>
                  <option value="create">Created</option>
                  <option value="edit">Edited</option>
                  <option value="delete">Deleted</option>
                  <option value="hide">Hidden</option>
                  <option value="show">Shown</option>
                  <option value="react">Reactions</option>
                </select>

                <select
                  value={postTypeFilter}
                  onChange={(e) => setPostTypeFilter(e.target.value)}
                  className="block w-auto pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                >
                  <option value="all">All Types</option>
                  <option value="timeline">Timeline</option>
                  <option value="meeting">Meeting</option>
                </select>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleExport}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Activities Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Posts Activities ({pagination.totalItems})
            </h3>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value) as 25 | 50 | 100 | 250)}
              className="block w-auto pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            >
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
              <option value={250}>250 per page</option>
            </select>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Post ID
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredActivities.map((activity) => (
                  <tr key={activity.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getActionColor(activity.action)}`}>
                        {getActionIcon(activity.action)}
                        <span className="ml-1">{formatActivityAction(activity)}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {activity.userName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatTimestamp(activity.timestamp)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
                        activity.postType === 'timeline' ? 'bg-purple-100 text-purple-800' : 'bg-orange-100 text-orange-800'
                      }`}>
                        {activity.postType === 'timeline' ? 'Timeline' : 'Meeting'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                      {activity.postId}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {getActivityDetails(activity)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
            <div className="flex flex-1 justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={getPaginationButtonClass(currentPage === 1)}
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === pagination.totalPages}
                className={getPaginationButtonClass(currentPage === pagination.totalPages)}
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{((currentPage - 1) * pageSize) + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * pageSize, pagination.totalItems)}
                  </span>{' '}
                  of <span className="font-medium">{pagination.totalItems}</span> results
                </p>
              </div>
              <div>
                <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={getPaginationButtonClass(currentPage === 1)}
                  >
                    Previous
                  </button>
                  {generatePageNumbers(currentPage, pagination.totalPages).map((page, index) => (
                    <button
                      key={index}
                      onClick={() => typeof page === 'number' ? handlePageChange(page) : undefined}
                      disabled={typeof page !== 'number'}
                      className={`${getPaginationButtonClass(typeof page !== 'number')} ${
                        page === currentPage ? 'bg-blue-50 border-blue-500 text-blue-600' : ''
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === pagination.totalPages}
                    className={getPaginationButtonClass(currentPage === pagination.totalPages)}
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PostsActivity; 